import { ConnectionKind } from "@redotech/redo-integrations-core/connection-type/connection-kind";
import { createConnectionType } from "@redotech/redo-integrations-core/connection-type/create-connection-type";
import * as z from "zod";

export const HTTP_AUTH_SCHEMES = ["bearer", "basic", "header"] as const;
export type HttpAuthScheme = (typeof HTTP_AUTH_SCHEMES)[number];

/** RFC 9110 `token`. */
const HEADER_NAME = /^[!#$%&'*+\-.^_`|~0-9A-Za-z]+$/;

/**
 * Headers a connection can't set: hop-by-hop headers (RFC 9110 §7.6.1), `Host`
 * and message framing. Lowercase.
 */
const FORBIDDEN_HTTP_HEADER_NAMES: ReadonlySet<string> = new Set([
  "connection",
  "content-length",
  "host",
  "keep-alive",
  "proxy-authenticate",
  "proxy-authorization",
  "proxy-connection",
  "te",
  "trailer",
  "transfer-encoding",
  "upgrade",
]);

/** C0 controls (including CR and LF) and DEL, which would split or corrupt a header. */
const CONTROL_CHARACTER = /[\u0000-\u001f\u007f]/;

const MAX_BASE_URL_LENGTH = 2048;

/** Suffixes that only resolve on a private network. */
const LOCAL_HOST_SUFFIXES = [".localhost", ".local", ".internal", ".home.arpa"];

/**
 * Whether `hostname` (as `URL` parses it) is an IP address or a local name. The
 * request path checks resolved addresses too; this rejects the obvious cases up front.
 */
function isLocalOrIpHost(hostname: string): boolean {
  const host = hostname.toLowerCase().replace(/\.$/, "");
  // `URL` normalizes IPv4 forms like `0x7f.1` and `2130706433` to dotted decimal and brackets IPv6.
  if (/^\d+\.\d+\.\d+\.\d+$/.test(host) || host.startsWith("[")) {
    return true;
  }
  return (
    !host.includes(".") ||
    LOCAL_HOST_SUFFIXES.some((suffix) => host.endsWith(suffix))
  );
}

/**
 * Why `raw` can't be a base URL, or null. It must be absolute `https://` with a
 * public domain name and no credentials, query or fragment.
 */
function baseUrlError(raw: string): string | null {
  if (raw.length > MAX_BASE_URL_LENGTH) {
    return `Base URL must be at most ${MAX_BASE_URL_LENGTH} characters`;
  }
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return "Base URL must be an absolute URL, like https://api.example.com/v1";
  }
  if (url.protocol !== "https:") {
    return "Base URL must start with https://";
  }
  if (!url.hostname) {
    return "Base URL must include a host";
  }
  if (isLocalOrIpHost(url.hostname)) {
    return "Base URL must use a public domain name, not an IP address or a local host";
  }
  // `URL` drops the default port, so `:443` leaves `port` empty.
  if (url.port !== "") {
    return "Only port 443 is supported";
  }
  // `URL` drops an empty userinfo (`https://@host`), so check the raw authority too.
  if (url.username || url.password || /^[^:]+:\/\/[^/]*@/.test(raw)) {
    return "Base URL can't include a username or password; use the auth scheme instead";
  }
  // `URL` drops an empty `?` or `#`, so check the raw text too.
  if (url.search || url.hash || raw.includes("?") || raw.includes("#")) {
    return "Base URL can't include a query string or fragment";
  }
  return null;
}

/** Origin and path, without a trailing slash, so request paths join predictably. */
function normalizeBaseUrl(raw: string): string {
  const url = new URL(raw);
  return `${url.origin}${url.pathname.replace(/\/+$/, "")}`;
}

const BaseUrl = z
  .string()
  .trim()
  .superRefine((raw, ctx) => {
    const error = baseUrlError(raw);
    if (error) {
      ctx.addIssue({ code: "custom", message: error });
    }
  })
  .transform(normalizeBaseUrl);

const HttpHeaderName = z
  .string()
  .trim()
  .min(1, "Enter a header name")
  .max(100)
  .regex(
    HEADER_NAME,
    "Header name can only use letters, digits and - _ . ! # $ % & ' * + ^ ` | ~",
  )
  .refine((name) => !FORBIDDEN_HTTP_HEADER_NAMES.has(name.toLowerCase()), {
    message: "This header is set by the connection itself and can't be used",
  });

const BasicUsername = z
  .string()
  .trim()
  .min(1, "Enter a username")
  .max(255)
  .refine((username) => !username.includes(":"), {
    message: "Username can't contain a colon",
  })
  .refine((username) => !CONTROL_CHARACTER.test(username), {
    message: "Username can't contain control characters",
  });

const HttpMetadata_ = z
  .object({
    baseUrl: BaseUrl,
    authScheme: z.enum(HTTP_AUTH_SCHEMES),
    // Null, not absent, when unused: updates merge metadata, so an absent key would keep a stale value.
    headerName: HttpHeaderName.nullable().default(null),
    username: BasicUsername.nullable().default(null),
  })
  .superRefine((metadata, ctx) => {
    if (metadata.authScheme === "header" && metadata.headerName === null) {
      ctx.addIssue({
        code: "custom",
        path: ["headerName"],
        message: "Enter the header name for the header scheme",
      });
    }
    if (metadata.authScheme !== "header" && metadata.headerName !== null) {
      ctx.addIssue({
        code: "custom",
        path: ["headerName"],
        message: "A header name is only used with the header scheme",
      });
    }
    if (metadata.authScheme === "basic" && metadata.username === null) {
      ctx.addIssue({
        code: "custom",
        path: ["username"],
        message: "Enter the username for basic auth",
      });
    }
    if (metadata.authScheme !== "basic" && metadata.username !== null) {
      ctx.addIssue({
        code: "custom",
        path: ["username"],
        message: "A username is only used with basic auth",
      });
    }
  });
export const HttpMetadata: z.ZodType<HttpMetadata, HttpMetadataInput> =
  HttpMetadata_;
export interface HttpMetadata extends z.output<typeof HttpMetadata_> {}
// Exported for declaration emit: `connectionTypes` infers it. //redontIgnore
export interface HttpMetadataInput extends z.input<typeof HttpMetadata_> {}

/** Sent as a header value, so it can't contain CR, LF or other controls. */
const HeaderSafeSecret = z
  .string()
  .trim()
  .min(1)
  .max(4096)
  .refine((value) => !CONTROL_CHARACTER.test(value), {
    message: "Can't contain line breaks or control characters",
  });

const HttpBearerSecret = z.strictObject({ token: HeaderSafeSecret });
/** Base64-encoded with the username, so any characters are safe. */
const HttpBasicSecret = z.strictObject({
  password: z.string().min(1).max(1024),
});
const HttpHeaderSecret = z.strictObject({ value: HeaderSafeSecret });

const HttpSecret_ = z.union([
  HttpBearerSecret,
  HttpBasicSecret,
  HttpHeaderSecret,
]);
export const HttpSecret: z.ZodType<HttpSecret, HttpSecret> = HttpSecret_;
export type HttpSecret = z.infer<typeof HttpSecret_>;

const secretFieldByScheme = {
  bearer: "token",
  basic: "password",
  header: "value",
} as const satisfies Record<HttpAuthScheme, string>;

/** The secret field an auth scheme uses: `token`, `password` or `value`. */
export function httpSecretField(scheme: HttpAuthScheme) {
  return secretFieldByScheme[scheme];
}

/** Why `secret` can't authenticate with `metadata`'s scheme, or null. */
export function httpSecretMismatch(
  metadata: HttpMetadata,
  secret: HttpSecret,
): string | null {
  const field = httpSecretField(metadata.authScheme);
  return field in secret
    ? null
    : `The ${metadata.authScheme} scheme needs a secret with only "${field}"`;
}

/** Why `current` can't be edited into `next`, or null. */
export function httpUpdateError(
  current: HttpMetadata,
  next: HttpMetadata,
): string | null {
  return current.authScheme === next.authScheme
    ? null
    : "The auth scheme can't change, since the stored secret belongs to it. Create a new connection instead.";
}

export const httpConnectionType = createConnectionType({
  kind: ConnectionKind.Http,
  displayName: "HTTP",
  metadata: HttpMetadata,
  secret: HttpSecret,
});
